import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Search, ChevronDown, X, Building2, User, Briefcase, FileText, Receipt, Wrench, GitBranch } from 'lucide-react';
import { apiGet } from '../lib/api';

/**
 * Department-aware "Link To" entity picker.
 *
 * Department → entity-type mapping (matches the spec from the user):
 *   Sales / BDM / Tendering            → lead, opportunity, company, contact
 *   Projects/Site / Engineering / Quality / Safety / Procurement
 *                                      → project, parent_task
 *   Finance & Accounts / Contracts & Legal
 *                                      → project, enquiry, invoice, ra_bill
 *   HR / Admin                         → employee, parent_task
 *   MD / super_admin / IT              → all of the above
 *
 * Props:
 *   value          { type, id, label } | null
 *   onChange       (selection|null) => void
 *   department     string – current user's department (drives the filter)
 *   role           string – 'super_admin', 'MD', 'user', etc.
 *   testid         automation hook
 */
const ENTITY_META = {
  lead:        { label: 'Lead',        icon: User,       endpoint: '/crm/leads',         labelKey: 'client', filterDepts: ['Business Development', 'Tendering', 'MD', 'IT'] },
  opportunity: { label: 'Opportunity', icon: Briefcase,  endpoint: '/crm/opportunities', labelKey: 'project_name', filterDepts: ['Business Development', 'Tendering', 'MD', 'IT'] },
  company:     { label: 'Company',     icon: Building2,  endpoint: '/crm/companies',     labelKey: 'name',   filterDepts: ['Business Development', 'Tendering', 'MD', 'IT'] },
  contact:     { label: 'Contact',     icon: User,       endpoint: '/crm/contacts',      labelKey: 'name',   filterDepts: ['Business Development', 'Tendering', 'MD', 'IT'] },
  project:     { label: 'Project',     icon: GitBranch,  endpoint: '/projects',          labelKey: 'name',   filterDepts: ['Projects/Site', 'Engineering', 'Quality', 'Safety', 'Procurement', 'Finance & Accounts', 'Contracts & Legal', 'MD', 'IT'] },
  enquiry:     { label: 'Enquiry',     icon: FileText,   endpoint: '/enquiries', labelKey: 'enquiry_no', secondaryKey: 'client', filterDepts: ['Tendering', 'Finance & Accounts', 'Contracts & Legal', 'MD', 'IT'] },
  invoice:     { label: 'Invoice',     icon: Receipt,    endpoint: '/invoices',          labelKey: 'invoice_no', filterDepts: ['Finance & Accounts', 'Contracts & Legal', 'MD', 'IT'] },
  ra_bill:     { label: 'RA Bill',     icon: Receipt,    endpoint: '/ra-bills',          labelKey: 'bill_no', filterDepts: ['Finance & Accounts', 'Contracts & Legal', 'Projects/Site', 'MD', 'IT'] },
  employee:    { label: 'Employee',    icon: User,       endpoint: '/hr/employees',      labelKey: 'name',   secondaryKey: 'emp_id', filterDepts: ['HR', 'Admin', 'MD', 'IT'] },
  parent_task: { label: 'Parent Task', icon: Wrench,     endpoint: '/crm/reminders?is_completed=false', labelKey: 'description', listKey: 'reminders', filterDepts: '__all__' },
};

function relevantEntities(department, role) {
  if (role === 'super_admin' || department === 'MD' || department === 'IT') {
    return Object.keys(ENTITY_META);
  }
  const dept = department || '';
  return Object.entries(ENTITY_META)
    .filter(([, meta]) => meta.filterDepts === '__all__' || meta.filterDepts.includes(dept))
    .map(([k]) => k);
}

let _cache = {};
async function fetchEntity(type) {
  const meta = ENTITY_META[type];
  if (!meta) return [];
  if (_cache[type]) return _cache[type];
  try {
    const res = await apiGet(meta.endpoint);
    // Some endpoints return {reminders:[...]} or {data:[...]} or just []
    const list = Array.isArray(res) ? res : (res?.[meta.listKey] || res?.data || res?.items || []);
    _cache[type] = list;
    return list;
  } catch {
    return [];
  }
}

export default function LinkedEntityPicker({ value, onChange, department, role, testid }) {
  const allowed = useMemo(() => relevantEntities(department, role), [department, role]);
  const [activeType, setActiveType] = useState(value?.type || allowed[0] || null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!activeType) return;
    setLoading(true);
    fetchEntity(activeType).then((list) => {
      setItems(list);
      setLoading(false);
    });
  }, [activeType]);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const meta = activeType ? ENTITY_META[activeType] : null;
  const filteredItems = useMemo(() => {
    if (!meta) return [];
    const q = query.trim().toLowerCase();
    let list = items;
    if (q) {
      list = list.filter((it) => {
        const primary = String(it[meta.labelKey] || '').toLowerCase();
        const secondary = meta.secondaryKey ? String(it[meta.secondaryKey] || '').toLowerCase() : '';
        return primary.includes(q) || secondary.includes(q);
      });
    }
    return list.slice(0, 30);
  }, [items, meta, query]);

  const commit = (item) => {
    const label = item[meta.labelKey] || item.id;
    onChange?.({ type: activeType, id: item.id, label });
    setOpen(false);
    setQuery('');
  };

  const clear = (e) => {
    e?.stopPropagation();
    onChange?.(null);
  };

  if (allowed.length === 0) return null;

  return (
    <div ref={rootRef} className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white hover:border-[#3b4872] focus:outline-none focus:border-[#3b4872]"
        data-testid={testid ? `${testid}-trigger` : undefined}
      >
        {value ? (
          <span className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-display font-bold uppercase tracking-[0.14em] text-[#3b4872] bg-indigo-50 px-1.5 py-0.5 rounded-sm shrink-0">
              {ENTITY_META[value.type]?.label || value.type}
            </span>
            <span className="truncate">{value.label}</span>
          </span>
        ) : (
          <span className="text-slate-400">Link to a record (optional)…</span>
        )}
        <span className="flex items-center gap-1 shrink-0">
          {value && (
            <span
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={(e) => { if (e.key === 'Enter') clear(e); }}
              className="p-0.5 text-slate-400 hover:text-slate-700 cursor-pointer"
              data-testid={testid ? `${testid}-clear` : undefined}
            >
              <X size={13} />
            </span>
          )}
          <ChevronDown size={13} className="text-slate-400" />
        </span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden">
          {/* Type tabs */}
          <div className="flex flex-wrap gap-1 p-2 border-b border-slate-100 bg-slate-50">
            {allowed.map((t) => {
              const Icon = ENTITY_META[t].icon;
              const active = t === activeType;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setActiveType(t); setQuery(''); }}
                  className={`flex items-center gap-1 text-[11px] font-display font-semibold uppercase tracking-wider px-2 py-1 rounded-sm border ${
                    active ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-700 border-slate-200 hover:border-[#3b4872]'
                  }`}
                  data-testid={testid ? `${testid}-tab-${t}` : undefined}
                >
                  <Icon size={11} />
                  {ENTITY_META[t].label}
                </button>
              );
            })}
          </div>

          <div className="relative border-b border-slate-100 px-2 py-1.5">
            <Search size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={meta ? `Search ${meta.label.toLowerCase()}…` : 'Search…'}
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-[#3b4872]"
              autoFocus
              data-testid={testid ? `${testid}-search` : undefined}
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-4 text-xs text-slate-500 text-center">Loading…</div>
            ) : filteredItems.length === 0 ? (
              <div className="px-3 py-4 text-xs text-slate-500 text-center">No results</div>
            ) : (
              filteredItems.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => commit(it)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 border-b border-slate-50"
                  data-testid={testid ? `${testid}-option-${it.id}` : undefined}
                >
                  <div className="font-medium text-slate-800 truncate">{it[meta.labelKey] || '(no label)'}</div>
                  {meta.secondaryKey && it[meta.secondaryKey] && (
                    <div className="text-[10px] text-slate-500 uppercase tracking-wide">{it[meta.secondaryKey]}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
